import { signal } from '@preact/signals';

/**
 * Minimal hand-rolled i18n — no library. A real i18n framework (icu-message
 * parsing, plural rules, lazy-loaded locale bundles) would cost more of the
 * 150 KB budget (ADR-0003) than this app's three fixed languages justify.
 */

export type Lang = 'en' | 'fr' | 'es';
export const LANGUAGES: Array<{ code: Lang; label: string; flag: string }> = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const LANG_KEY = 'foyer_lang';

function detectInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'fr' || stored === 'es') return stored;
  } catch {
    // localStorage can throw in a locked-down context (private mode, quota) — fall through.
  }
  const nav = navigator.language.slice(0, 2);
  if (nav === 'fr' || nav === 'es') return nav;
  return 'en';
}

export const lang = signal<Lang>(detectInitialLang());

export function setLang(l: Lang): void {
  lang.value = l;
  try {
    localStorage.setItem(LANG_KEY, l);
  } catch {
    // Per-device preference only; losing it just means it resets next visit.
  }
  document.documentElement.lang = l;
}

document.documentElement.lang = lang.value;

type Dict = Record<string, string>;

const en: Dict = {
  'app.loading': 'loading…',
  'app.tagline': 'the hearth your family gathers around',
  'auth.signIn': 'Sign in',
  'auth.haveInvite': 'I have an invite',
  'auth.inviteCode': 'Invite code',
  'auth.yourName': 'Your name',
  'auth.handle': 'Handle',
  'auth.password': 'Password',
  'auth.pleaseWait': 'Please wait…',
  'auth.createAccount': 'Create account',
  'conn.open': 'Connected',
  'conn.connecting': 'Connecting…',
  'conn.reconnecting': 'Reconnecting…',
  'conn.closed': 'Offline',
  'chat.familyTitle': 'Family',
  'chat.invite': 'Invite',
  'chat.settings': 'Settings',
  'chat.signOut': 'Sign out',
  'chat.compressingPhoto': 'Compressing photo {index} of {total}…',
  'chat.uploadingPhoto': 'Sending photo {index} of {total}…',
  'chat.inviteCreated': "Invite code: {code} (valid 7 days)",
  'chat.inviteFailed': 'Could not create an invite.',
  'chat.messagePlaceholder': 'Message…',
  'chat.captionPlaceholder': 'Add a caption…',
  'chat.send': 'Send',
  'chat.sendPhotos': 'Send photos',
  'reply.action': 'Reply',
  'reply.cancel': 'Cancel reply',
  'reply.unavailable': 'Original message',
  'reply.photo': 'Photo',
  'meta.sending': 'sending…',
  'meta.sent': 'sent',
  'meta.delivered': 'delivered',
  'meta.read': 'read',
  'device.android': 'Android phone',
  'device.ios': 'iPhone/iPad',
  'device.browser': 'Browser',
  'viewer.close': 'Close',
  'viewer.download': 'Download photo',
  'viewer.prev': 'Previous photo',
  'viewer.next': 'Next photo',
  'viewer.loadError': 'Could not load the photo.',
  'viewer.loading': 'Loading…',
  'viewer.menu.download': 'Download photo',
  'settings.title': 'Settings',
  'settings.back': 'Back',
  'settings.language': 'Language',
  'settings.appliesInstantly': 'Applied instantly, nothing to save here.',
  'settings.textSize': 'Text size',
  'settings.textSize.condensed': 'Condensed',
  'settings.textSize.medium': 'Medium',
  'settings.textSize.large': 'Large',
  'settings.account': 'Account',
  'settings.signedInAs': 'Signed in as {name}',
  'settings.changePassword': 'Change password',
  'settings.currentPassword': 'Current password',
  'settings.newPassword': 'New password',
  'settings.confirmPassword': 'Confirm password',
  'settings.passwordChanged': 'Password changed. Other devices have been signed out.',
  'settings.passwordMismatch': "Passwords don't match.",
  'settings.save': 'Save',
  'appLock.title': 'App lock',
  'appLock.explain':
    "A quick unlock for this device only — fingerprint, face, or a 4-digit PIN. It doesn't replace your password.",
  'appLock.pinIsSet': 'PIN set',
  'appLock.biometricIsSet': 'Fingerprint/face set',
  'appLock.newPin': 'New 4-digit PIN',
  'appLock.confirmPin': 'Confirm PIN',
  'appLock.setPin': 'Set PIN',
  'appLock.pinSet': 'PIN set.',
  'appLock.pinInvalid': 'Enter exactly 4 digits.',
  'appLock.pinMismatch': "PINs don't match.",
  'appLock.registerBiometric': 'Use fingerprint / face',
  'appLock.biometricRegistered': 'Fingerprint/face unlock is set up.',
  'appLock.biometricFailed': 'Could not set up fingerprint/face on this device.',
  'appLock.disable': 'Turn off app lock',
  'appLock.enterPin': 'Enter your PIN',
  'appLock.wrongPin': 'Wrong PIN.',
  'appLock.unlock': 'Unlock',
  'appLock.useBiometric': 'Use fingerprint / face instead',
  'appLock.usePinInstead': 'Use PIN instead',
  'appLock.waitingBiometric': 'Waiting for fingerprint / face…',
  'err.invalid_invite': 'That invite code is invalid or has expired.',
  'err.handle_taken': 'That name is already taken.',
  'err.invalid_credentials': 'Wrong name or password.',
  'err.unauthorized': 'Sign in required.',
  'err.invalid_request': 'Invalid request.',
  'err.password_too_short': 'Password must be at least 10 characters.',
  'err.invalid_handle': 'Invalid handle (lowercase letters, numbers, - and _ only).',
  'err.current_password_required': 'Enter your current password.',
  'err.too_large': 'This file is too large.',
  'err.disk_full': 'The server is low on disk space. Try again later.',
  'err.quota_exceeded': 'The family storage quota is full.',
  'err.user_quota_exceeded': 'Your personal storage quota is full.',
  'err.hash_mismatch': "The received file doesn't match.",
  'err.upload_failed': 'The upload failed.',
  'err.not_found': 'Not found.',
  'err.forbidden': 'Access denied.',
  'err.bad_origin': 'Origin not allowed.',
  'err.invalid_refresh_token': 'Session expired, please sign in again.',
  'err.no_refresh_token': 'Session expired, please sign in again.',
  'err.device_revoked': 'This device has been signed out.',
  'err.internal_error': 'Something went wrong on the server.',
  'err.generic': 'Something went wrong.',
};

const fr: Dict = {
  'app.loading': 'chargement…',
  'app.tagline': 'le foyer où votre famille se réunit',
  'auth.signIn': 'Se connecter',
  'auth.haveInvite': "J'ai une invitation",
  'auth.inviteCode': "Code d'invitation",
  'auth.yourName': 'Votre nom',
  'auth.handle': 'Identifiant',
  'auth.password': 'Mot de passe',
  'auth.pleaseWait': 'Veuillez patienter…',
  'auth.createAccount': 'Créer un compte',
  'conn.open': 'Connecté',
  'conn.connecting': 'Connexion…',
  'conn.reconnecting': 'Reconnexion…',
  'conn.closed': 'Hors ligne',
  'chat.familyTitle': 'Famille',
  'chat.invite': 'Inviter',
  'chat.settings': 'Réglages',
  'chat.signOut': 'Se déconnecter',
  'chat.compressingPhoto': 'Compression de la photo {index} sur {total}…',
  'chat.uploadingPhoto': 'Envoi de la photo {index} sur {total}…',
  'chat.inviteCreated': "Code d'invitation : {code} (valide 7 jours)",
  'chat.inviteFailed': "Impossible de créer une invitation.",
  'chat.messagePlaceholder': 'Message…',
  'chat.captionPlaceholder': 'Ajouter une légende…',
  'chat.send': 'Envoyer',
  'chat.sendPhotos': 'Envoyer des photos',
  'reply.action': 'Répondre',
  'reply.cancel': 'Annuler la réponse',
  'reply.unavailable': 'Message original',
  'reply.photo': 'Photo',
  'meta.sending': 'envoi…',
  'meta.sent': 'envoyé',
  'meta.delivered': 'distribué',
  'meta.read': 'lu',
  'device.android': 'Téléphone Android',
  'device.ios': 'iPhone/iPad',
  'device.browser': 'Navigateur',
  'viewer.close': 'Fermer',
  'viewer.download': 'Télécharger la photo',
  'viewer.prev': 'Photo précédente',
  'viewer.next': 'Photo suivante',
  'viewer.loadError': 'Impossible de charger la photo.',
  'viewer.loading': 'Chargement…',
  'viewer.menu.download': 'Télécharger la photo',
  'settings.title': 'Réglages',
  'settings.back': 'Retour',
  'settings.language': 'Langue',
  'settings.appliesInstantly': 'Appliqué immédiatement, rien à enregistrer ici.',
  'settings.textSize': 'Taille du texte',
  'settings.textSize.condensed': 'Condensée',
  'settings.textSize.medium': 'Moyenne',
  'settings.textSize.large': 'Grande',
  'settings.account': 'Compte',
  'settings.signedInAs': 'Connecté en tant que {name}',
  'settings.changePassword': 'Changer le mot de passe',
  'settings.currentPassword': 'Mot de passe actuel',
  'settings.newPassword': 'Nouveau mot de passe',
  'settings.confirmPassword': 'Confirmer le mot de passe',
  'settings.passwordChanged': 'Mot de passe modifié. Les autres appareils ont été déconnectés.',
  'settings.passwordMismatch': 'Les mots de passe ne correspondent pas.',
  'settings.save': 'Enregistrer',
  'appLock.title': 'Verrouillage de l’appli',
  'appLock.explain':
    "Un déverrouillage rapide pour cet appareil uniquement — empreinte, visage ou code à 4 chiffres. Ne remplace pas ton mot de passe.",
  'appLock.pinIsSet': 'Code défini',
  'appLock.biometricIsSet': 'Empreinte/visage défini',
  'appLock.newPin': 'Nouveau code à 4 chiffres',
  'appLock.confirmPin': 'Confirmer le code',
  'appLock.setPin': 'Définir le code',
  'appLock.pinSet': 'Code défini.',
  'appLock.pinInvalid': 'Entre exactement 4 chiffres.',
  'appLock.pinMismatch': 'Les codes ne correspondent pas.',
  'appLock.registerBiometric': 'Utiliser empreinte / visage',
  'appLock.biometricRegistered': "Le déverrouillage par empreinte/visage est configuré.",
  'appLock.biometricFailed': 'Impossible de configurer empreinte/visage sur cet appareil.',
  'appLock.disable': "Désactiver le verrouillage",
  'appLock.enterPin': 'Entre ton code',
  'appLock.wrongPin': 'Code incorrect.',
  'appLock.unlock': 'Déverrouiller',
  'appLock.useBiometric': 'Utiliser empreinte / visage à la place',
  'appLock.usePinInstead': 'Utiliser le code à la place',
  'appLock.waitingBiometric': 'En attente empreinte / visage…',
  'err.invalid_invite': "Ce code d'invitation est invalide ou a expiré.",
  'err.handle_taken': 'Ce nom est déjà pris.',
  'err.invalid_credentials': 'Nom ou mot de passe incorrect.',
  'err.unauthorized': 'Connexion requise.',
  'err.invalid_request': 'Requête invalide.',
  'err.password_too_short': 'Le mot de passe doit contenir au moins 10 caractères.',
  'err.invalid_handle': 'Identifiant invalide (lettres minuscules, chiffres, - et _ uniquement).',
  'err.current_password_required': 'Indique ton mot de passe actuel.',
  'err.too_large': 'Ce fichier est trop volumineux.',
  'err.disk_full': "Le serveur manque d'espace disque. Réessaie plus tard.",
  'err.quota_exceeded': 'Le quota de stockage familial est atteint.',
  'err.user_quota_exceeded': 'Ton quota de stockage personnel est atteint.',
  'err.hash_mismatch': 'Le fichier reçu ne correspond pas.',
  'err.upload_failed': 'Le téléversement a échoué.',
  'err.not_found': 'Introuvable.',
  'err.forbidden': 'Accès refusé.',
  'err.bad_origin': 'Origine non autorisée.',
  'err.invalid_refresh_token': 'Session expirée, reconnecte-toi.',
  'err.no_refresh_token': 'Session expirée, reconnecte-toi.',
  'err.device_revoked': 'Cet appareil a été déconnecté.',
  'err.internal_error': 'Une erreur est survenue sur le serveur.',
  'err.generic': 'Une erreur est survenue.',
};

const es: Dict = {
  'app.loading': 'cargando…',
  'app.tagline': 'el hogar donde tu familia se reúne',
  'auth.signIn': 'Iniciar sesión',
  'auth.haveInvite': 'Tengo una invitación',
  'auth.inviteCode': 'Código de invitación',
  'auth.yourName': 'Tu nombre',
  'auth.handle': 'Usuario',
  'auth.password': 'Contraseña',
  'auth.pleaseWait': 'Espera…',
  'auth.createAccount': 'Crear cuenta',
  'conn.open': 'Conectado',
  'conn.connecting': 'Conectando…',
  'conn.reconnecting': 'Reconectando…',
  'conn.closed': 'Sin conexión',
  'chat.familyTitle': 'Familia',
  'chat.invite': 'Invitar',
  'chat.settings': 'Ajustes',
  'chat.signOut': 'Cerrar sesión',
  'chat.compressingPhoto': 'Comprimiendo foto {index} de {total}…',
  'chat.uploadingPhoto': 'Enviando foto {index} de {total}…',
  'chat.inviteCreated': 'Código de invitación: {code} (válido 7 días)',
  'chat.inviteFailed': 'No se pudo crear una invitación.',
  'chat.messagePlaceholder': 'Mensaje…',
  'chat.captionPlaceholder': 'Añadir un pie de foto…',
  'chat.send': 'Enviar',
  'chat.sendPhotos': 'Enviar fotos',
  'reply.action': 'Responder',
  'reply.cancel': 'Cancelar respuesta',
  'reply.unavailable': 'Mensaje original',
  'reply.photo': 'Foto',
  'meta.sending': 'enviando…',
  'meta.sent': 'enviado',
  'meta.delivered': 'entregado',
  'meta.read': 'leído',
  'device.android': 'Teléfono Android',
  'device.ios': 'iPhone/iPad',
  'device.browser': 'Navegador',
  'viewer.close': 'Cerrar',
  'viewer.download': 'Descargar foto',
  'viewer.prev': 'Foto anterior',
  'viewer.next': 'Foto siguiente',
  'viewer.loadError': 'No se pudo cargar la foto.',
  'viewer.loading': 'Cargando…',
  'viewer.menu.download': 'Descargar foto',
  'settings.title': 'Ajustes',
  'settings.back': 'Volver',
  'settings.language': 'Idioma',
  'settings.appliesInstantly': 'Se aplica al instante, no hay nada que guardar aquí.',
  'settings.textSize': 'Tamaño del texto',
  'settings.textSize.condensed': 'Condensado',
  'settings.textSize.medium': 'Medio',
  'settings.textSize.large': 'Grande',
  'settings.account': 'Cuenta',
  'settings.signedInAs': 'Sesión iniciada como {name}',
  'settings.changePassword': 'Cambiar contraseña',
  'settings.currentPassword': 'Contraseña actual',
  'settings.newPassword': 'Nueva contraseña',
  'settings.confirmPassword': 'Confirmar contraseña',
  'settings.passwordChanged': 'Contraseña cambiada. Se cerró la sesión en otros dispositivos.',
  'settings.passwordMismatch': 'Las contraseñas no coinciden.',
  'settings.save': 'Guardar',
  'appLock.title': 'Bloqueo de la app',
  'appLock.explain':
    'Un desbloqueo rápido solo para este dispositivo — huella, cara o un PIN de 4 dígitos. No reemplaza tu contraseña.',
  'appLock.pinIsSet': 'PIN configurado',
  'appLock.biometricIsSet': 'Huella/cara configurada',
  'appLock.newPin': 'Nuevo PIN de 4 dígitos',
  'appLock.confirmPin': 'Confirmar PIN',
  'appLock.setPin': 'Configurar PIN',
  'appLock.pinSet': 'PIN configurado.',
  'appLock.pinInvalid': 'Ingresa exactamente 4 dígitos.',
  'appLock.pinMismatch': 'Los PIN no coinciden.',
  'appLock.registerBiometric': 'Usar huella / cara',
  'appLock.biometricRegistered': 'El desbloqueo por huella/cara está configurado.',
  'appLock.biometricFailed': 'No se pudo configurar huella/cara en este dispositivo.',
  'appLock.disable': 'Desactivar bloqueo',
  'appLock.enterPin': 'Ingresa tu PIN',
  'appLock.wrongPin': 'PIN incorrecto.',
  'appLock.unlock': 'Desbloquear',
  'appLock.useBiometric': 'Usar huella / cara en su lugar',
  'appLock.usePinInstead': 'Usar el PIN en su lugar',
  'appLock.waitingBiometric': 'Esperando huella / cara…',
  'err.invalid_invite': 'Este código de invitación no es válido o ha expirado.',
  'err.handle_taken': 'Ese nombre ya está en uso.',
  'err.invalid_credentials': 'Nombre o contraseña incorrectos.',
  'err.unauthorized': 'Inicio de sesión requerido.',
  'err.invalid_request': 'Solicitud inválida.',
  'err.password_too_short': 'La contraseña debe tener al menos 10 caracteres.',
  'err.invalid_handle': 'Usuario inválido (minúsculas, números, - y _ solamente).',
  'err.current_password_required': 'Ingresa tu contraseña actual.',
  'err.too_large': 'El archivo es demasiado grande.',
  'err.disk_full': 'El servidor no tiene espacio en disco. Inténtalo más tarde.',
  'err.quota_exceeded': 'Se alcanzó la cuota de almacenamiento familiar.',
  'err.user_quota_exceeded': 'Se alcanzó tu cuota de almacenamiento personal.',
  'err.hash_mismatch': 'El archivo recibido no coincide.',
  'err.upload_failed': 'La subida falló.',
  'err.not_found': 'No encontrado.',
  'err.forbidden': 'Acceso denegado.',
  'err.bad_origin': 'Origen no permitido.',
  'err.invalid_refresh_token': 'Sesión expirada, vuelve a iniciar sesión.',
  'err.no_refresh_token': 'Sesión expirada, vuelve a iniciar sesión.',
  'err.device_revoked': 'Este dispositivo cerró sesión.',
  'err.internal_error': 'Ocurrió un error en el servidor.',
  'err.generic': 'Ocurrió un error.',
};

const dictionaries: Record<Lang, Dict> = { en, fr, es };

/** Reads `lang.value` — called during render, this makes the caller reactive
 * to language changes automatically (Preact signals subscribe on `.value` read). */
export function t(key: string, vars?: Record<string, string | number>): string {
  let str = dictionaries[lang.value][key] ?? dictionaries.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) str = str.replace(`{${k}}`, String(v));
  }
  return str;
}

/** Translates a server error code, falling back to its raw message for an unmapped code. */
export function tError(code: string | undefined, fallbackMessage: string): string {
  if (code && dictionaries[lang.value][`err.${code}`]) return t(`err.${code}`);
  return fallbackMessage;
}
